import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Chip,
  Fade,
  Zoom,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tooltip,
} from "@mui/material";
import axios from "axios";
import { backend_url, googleMapsApiKey } from "../App";
import {
  CellTower as CellTowerIcon,
  Refresh as RefreshIcon,
  Science as ScienceIcon,
  Dashboard as DashboardIcon,
  Close as CloseIcon,
  LocationOn as LocationIcon,
} from "@mui/icons-material";

export default function WeatherStations() {
  const [towers, setTowers] = useState([]);
  const [sensors, setSensors] = useState([]);
  const [filteredSensors, setFilteredSensors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterType, setFilterType] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTower, setSelectedTower] = useState("all");
  const [dashboardOpen, setDashboardOpen] = useState(false);
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [selectedSensor, setSelectedSensor] = useState(null);
  const [map, setMap] = useState(null);
  const [marker, setMarker] = useState(null);
  const mapInitializedRef = useRef(false);

  // Dashboard link
  const dashboardLink =
    "https://icarus.lums.edu.pk/dashboard/c3e6ece0-539c-11f0-af37-719941c4ace8?publicId=05acac90-5beb-11f0-af37-719941c4ace8&state=W3siaWQiOiJkZWZhdWx0IiwicGFyYW1zIjp7ImVudGl0eUlkIjp7ImlkIjoiNDk1Y2RhZTEtZWY3Yy0xMWVmLWFmMzctNzE5OTQxYzRhY2U4IiwiZW50aXR5VHlwZSI6IkRFVklDRSJ9LCJlbnRpdHlOYW1lIjoiMjUwMi1MTEctQVd9fV0%3D";

  // 3D effect styles
  const card3D = {
    background: "linear-gradient(145deg, #ffffff 0%, #f5f5f5 100%)",
    borderRadius: "20px",
    boxShadow: "20px 20px 60px #d9d9d9, -20px -20px 60px #ffffff",
    transition: "transform 0.3s ease, box-shadow 0.3s ease",
    "&:hover": {
      transform: "translateY(-5px)",
      boxShadow: "25px 25px 75px #d9d9d9, -25px -25px 75px #ffffff",
    },
  };

  // Load Google Maps API
  useEffect(() => {
    const loadGoogleMaps = () => {
      if (!document.querySelector(`script[src*="maps.googleapis.com"]`)) {
        const script = document.createElement("script");
        script.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}`;
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      }
    };
    loadGoogleMaps();
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    console.log("🔄 Fetching weather station data...");

    try {
      // Fetch towers
      console.log(
        "📡 Fetching towers from:",
        `${backend_url}/core/api/towers/`,
      );
      const towersResponse = await axios.get(
        `${backend_url}/core/api/towers/`,
        {
          headers: {
            Authorization: `Token ${localStorage.getItem("token")}`,
          },
        },
      );

      console.log("✅ Towers response:", towersResponse.data);
      const towersData =
        towersResponse.data.results || towersResponse.data || [];
      setTowers(towersData);

      // Extract all sensors from towers
      let allSensors = [];
      towersData.forEach((tower) => {
        if (tower.sensors && tower.sensors.length > 0) {
          const towerSensors = tower.sensors.map((sensor) => ({
            ...sensor,
            tower_id: tower.id,
            tower_name: tower.name,
            tower_display: `T${tower.id} - ${tower.name}`,
            tower_lat: tower.lat,
            tower_lng: tower.lng,
          }));
          allSensors = [...allSensors, ...towerSensors];
        }
      });

      console.log("📊 Extracted sensors from towers:", allSensors);
      setSensors(allSensors);
      setFilteredSensors(allSensors);
    } catch (err) {
      console.error("❌ Error fetching weather station data:", err);
      console.error("Error details:", {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
      });
      setError(
        err.response?.data?.detail ||
          err.message ||
          "Failed to load weather station data",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();

    // Refresh data every 5 minutes
    const interval = setInterval(fetchData, 300000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Apply filters when dependencies change
  useEffect(() => {
    if (sensors.length > 0) {
      console.log("🔍 Applying filters:", {
        filterType,
        selectedTower,
        searchTerm,
      });

      let filtered = [...sensors];

      // Filter by sensor type
      if (filterType !== "all") {
        filtered = filtered.filter(
          (s) => s.sensor_type?.toLowerCase() === filterType.toLowerCase(),
        );
      }

      // Filter by tower
      if (selectedTower !== "all") {
        filtered = filtered.filter(
          (s) => s.tower_id === parseInt(selectedTower),
        );
      }

      // Filter by search term
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        filtered = filtered.filter(
          (s) =>
            s.name?.toLowerCase().includes(term) ||
            (s.tower_name && s.tower_name.toLowerCase().includes(term)) ||
            s.sensor_type?.toLowerCase().includes(term) ||
            s.id?.toString().includes(term),
        );
      }

      console.log("✅ Filtered sensors:", filtered.length);
      setFilteredSensors(filtered);
      setPage(0);
    }
  }, [sensors, filterType, selectedTower, searchTerm]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRefresh = () => {
    fetchData();
  };

  const handleOpenDashboard = () => {
    setDashboardOpen(true);
  };

  const handleCloseDashboard = () => {
    setDashboardOpen(false);
  };

  const handleOpenMap = (sensor) => {
    setSelectedSensor(sensor);
    setMapModalOpen(true);
    mapInitializedRef.current = false; // Reset for new sensor
  };

  const handleCloseMap = () => {
    setMapModalOpen(false);
    setSelectedSensor(null);
    // Clean up map
    if (map) {
      setMap(null);
    }
    if (marker) {
      setMarker(null);
    }
    mapInitializedRef.current = false;
  };

  // Initialize map when modal opens - function defined inside useEffect to avoid dependency warning
  useEffect(() => {
    if (mapModalOpen && selectedSensor && window.google) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        if (!mapInitializedRef.current) {
          const mapElement = document.getElementById("sensor-map");
          if (!mapElement) return;

          const lat = selectedSensor.lat || selectedSensor.tower_lat;
          const lng = selectedSensor.lng || selectedSensor.tower_lng;
          const position = { lat: parseFloat(lat), lng: parseFloat(lng) };

          const newMap = new window.google.maps.Map(mapElement, {
            zoom: 15,
            center: position,
            styles: [
              {
                featureType: "all",
                elementType: "labels.text.fill",
                stylers: [{ color: "#333333" }],
              },
              {
                featureType: "landscape",
                elementType: "all",
                stylers: [{ color: "#f2f2f2" }],
              },
              {
                featureType: "poi",
                elementType: "all",
                stylers: [{ visibility: "off" }],
              },
              {
                featureType: "road",
                elementType: "all",
                stylers: [{ saturation: -100 }, { lightness: 45 }],
              },
              {
                featureType: "transit",
                elementType: "all",
                stylers: [{ visibility: "off" }],
              },
            ],
            mapTypeControl: true,
            mapTypeControlOptions: {
              style: window.google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
              position: window.google.maps.ControlPosition.TOP_RIGHT,
            },
            zoomControl: true,
            zoomControlOptions: {
              position: window.google.maps.ControlPosition.RIGHT_CENTER,
            },
            scaleControl: true,
            streetViewControl: true,
            fullscreenControl: true,
          });

          const newMarker = new window.google.maps.Marker({
            position: position,
            map: newMap,
            title:
              selectedSensor.name || `${selectedSensor.sensor_type} Sensor`,
            animation: window.google.maps.Animation.DROP,
            icon: {
              url: "http://maps.google.com/mapfiles/ms/icons/red-dot.png",
              scaledSize: new window.google.maps.Size(40, 40),
            },
          });

          // Add info window
          const infoWindow = new window.google.maps.InfoWindow({
            content: `
              <div style="padding: 10px; min-width: 200px;">
                <h4 style="margin: 0 0 8px 0; color: #333;">${selectedSensor.name || `${selectedSensor.sensor_type} Sensor`}</h4>
                <p style="margin: 5px 0; color: #666;">
                  <strong>Type:</strong> ${selectedSensor.sensor_type || "Unknown"}<br>
                  <strong>Tower:</strong> ${selectedSensor.tower_display || `T${selectedSensor.tower_id}`}<br>
                  <strong>Coordinates:</strong><br>
                  Lat: ${lat?.toFixed(6)}<br>
                  Lng: ${lng?.toFixed(6)}
                </p>
              </div>
            `,
          });

          newMarker.addListener("click", () => {
            infoWindow.open(newMap, newMarker);
          });

          setMap(newMap);
          setMarker(newMarker);
          mapInitializedRef.current = true;
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [mapModalOpen, selectedSensor]); // Only depends on these two values

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 2, textAlign: "center", py: 6 }}>
        <Zoom in={true}>
          <Box>
            <CircularProgress size={80} sx={{ color: "#454545", mb: 3 }} />
            <Typography variant="h5" sx={{ color: "#666", mb: 2 }}>
              Loading Weather Stations
            </Typography>
            <LinearProgress
              sx={{
                maxWidth: 400,
                mx: "auto",
                borderRadius: 10,
                "& .MuiLinearProgress-bar": {
                  background: "linear-gradient(90deg, #ff8c00, #454545)",
                },
              }}
            />
            <Typography variant="body2" sx={{ color: "#999", mt: 2 }}>
              Fetching tower and sensor data...
            </Typography>
          </Box>
        </Zoom>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ mt: 2 }}>
        <Fade in={true}>
          <Alert
            severity="error"
            sx={{
              borderRadius: "16px",
              boxShadow: "0 4px 20px rgba(244,67,54,0.2)",
            }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={handleRefresh}
                startIcon={<RefreshIcon />}
              >
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        </Fade>
      </Container>
    );
  }

  return (
    <Fade in={true}>
      <Container maxWidth="xl" sx={{ mb: 1 }}>
        {/* View Dashboard Button */}
        <Box sx={{ mb: 1 }}>
          <Button
            variant="contained"
            fullWidth
            startIcon={<DashboardIcon />}
            onClick={handleOpenDashboard}
            sx={{
              bgcolor: "#454545",
              color: "white",
              borderRadius: "30px",
              px: 1,
              "&:hover": {
                bgcolor: "#5a5555",
              },
            }}
          >
            View Dashboard
          </Button>
        </Box>

        {/* Filters */}
        <Paper
          sx={{
            p: 2,
            mb: 1,
            ...card3D,
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Sensor Type</InputLabel>
                <Select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  label="Sensor Type"
                  sx={{ borderRadius: "25px" }}
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="temperature">Temperature</MenuItem>
                  <MenuItem value="humidity">Humidity</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Tower</InputLabel>
                <Select
                  value={selectedTower}
                  onChange={(e) => setSelectedTower(e.target.value)}
                  label="Tower"
                  sx={{ borderRadius: "25px" }}
                >
                  <MenuItem value="all">All Towers</MenuItem>
                  {towers.map((tower) => (
                    <MenuItem key={tower.id} value={tower.id}>
                      T{tower.id} - {tower.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Search Sensors"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                // size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "25px",
                    height: 40,
                    textAlign: "center",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={2}>
              <Paper
                sx={{
                  p: 1,
                  bgcolor: "#f5f5f5",
                  borderRadius: "25px",
                  textAlign: "center",
                }}
              >
                <Typography variant="body2" sx={{ color: "#666" }}>
                  {filteredSensors.length} sensor
                  {filteredSensors.length !== 1 ? "s" : ""}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Paper>

        {/* Sensors Table */}
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: "20px",
            overflow: "hidden",
            ...card3D,
          }}
        >
          <Table>
            <TableHead
              sx={{
                bgcolor: "#f5f5f5",
                borderBottom: "2px solid #454545",
              }}
            >
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>ID</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Sensor Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Tower</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Location</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredSensors
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((sensor, index) => {
                  const hasCoordinates = sensor.lat && sensor.lng;
                  return (
                    <Fade
                      in={true}
                      key={sensor.id}
                      style={{ transitionDelay: `${index * 30}ms` }}
                    >
                      <TableRow
                        sx={{
                          "&:hover": {
                            bgcolor: "#f8f9fa",
                            transform: "scale(1.01)",
                            transition: "all 0.2s ease",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                          },
                        }}
                      >
                        <TableCell>
                          <Chip
                            label={`#${sensor.id}`}
                            size="small"
                            sx={{
                              bgcolor: "#f0f0f0",
                              fontWeight: 600,
                              fontFamily: "monospace",
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {sensor.name || `${sensor.sensor_type} Sensor`}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={sensor.sensor_type || "Unknown"}
                            size="small"
                            sx={{
                              bgcolor:
                                sensor.sensor_type === "Temperature"
                                  ? "#ff9800"
                                  : "#2196f3",
                              color: "white",
                              fontWeight: 500,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <CellTowerIcon
                              sx={{ fontSize: 16, color: "#666" }}
                            />
                            <Typography variant="body2">
                              {sensor.tower_display ||
                                `T${sensor.tower_id} - ${sensor.tower_name}`}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {hasCoordinates ? (
                            <Tooltip title="View on Map">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenMap(sensor)}
                                sx={{
                                  color: "#454545",
                                  "&:hover": {
                                    backgroundColor: "rgba(69,69,69,0.1)",
                                    transform: "scale(1.1)",
                                  },
                                }}
                              >
                                <LocationIcon />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Typography
                              variant="caption"
                              sx={{ color: "#999" }}
                            >
                              No coordinates
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    </Fade>
                  );
                })}

              {filteredSensors.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <Zoom in={true}>
                      <Box>
                        <ScienceIcon
                          sx={{ fontSize: 60, color: "#ccc", mb: 2 }}
                        />
                        <Typography variant="h6" sx={{ color: "#666", mb: 1 }}>
                          No sensors found
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#999" }}>
                          Try adjusting your filters or refresh the data
                        </Typography>
                        <Button
                          variant="outlined"
                          startIcon={<RefreshIcon />}
                          onClick={handleRefresh}
                          sx={{ mt: 2, borderRadius: "25px" }}
                        >
                          Refresh
                        </Button>
                      </Box>
                    </Zoom>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={filteredSensors.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: "1px solid #f0f0f0",
              ".MuiTablePagination-select": {
                borderRadius: "15px",
              },
            }}
          />
        </TableContainer>

        {/* Dashboard Modal */}
        <Dialog
          open={dashboardOpen}
          onClose={handleCloseDashboard}
          maxWidth="xl"
          fullWidth
          PaperProps={{
            sx: {
              height: "95vh",
              bgcolor: "#1e1e1e",
              borderRadius: "20px",
              overflow: "hidden",
            },
          }}
        >
          <DialogTitle
            sx={{
              bgcolor: "#2a2a2a",
              color: "white",
              borderBottom: "1px solid #333",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              py: 1,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Weather Station Dashboard
            </Typography>
            <IconButton
              aria-label="close"
              onClick={handleCloseDashboard}
              sx={{
                color: "#888",
                "&:hover": {
                  color: "#454545",
                  backgroundColor: "rgba(255,68,68,0.1)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent
            sx={{ p: 0, bgcolor: "#1e1e1e", height: "calc(95vh - 0px)" }}
          >
            <iframe
              src={dashboardLink}
              title="Weather Dashboard"
              width="100%"
              height="98%"
              style={{
                border: "none",
                backgroundColor: "#1e1e1e",
              }}
              sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
            />
          </DialogContent>
        </Dialog>

        {/* Map Modal for Sensor Location */}
        <Dialog
          open={mapModalOpen}
          onClose={handleCloseMap}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              height: "70vh",
              borderRadius: "20px",
              overflow: "hidden",
            },
          }}
        >
          <DialogTitle
            sx={{
              bgcolor: "#454545",
              color: "white",
              borderBottom: "1px solid #666",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              py: 1.5,
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <LocationIcon />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Sensor Location -{" "}
                {selectedSensor?.name ||
                  `${selectedSensor?.sensor_type} Sensor`}
              </Typography>
            </Box>
            <IconButton
              aria-label="close"
              onClick={handleCloseMap}
              sx={{
                color: "white",
                "&:hover": {
                  color: "#ff4444",
                  backgroundColor: "rgba(255,255,255,0.1)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0, position: "relative" }}>
            <div
              id="sensor-map"
              style={{
                width: "100%",
                height: "100%",
                minHeight: "500px",
              }}
            />
          </DialogContent>
        </Dialog>
      </Container>
    </Fade>
  );
}
